import * as React from "react";
import { Modal } from "react-bootstrap";
import { isIOS, isMobile } from "react-device-detect";
import {
  AuthenticationClient,
  CollectStatus,
  Order,
  SignInitiateRequest,
} from "../api/ApiClient";
import { parseQueryString } from "../services/QueryString";
import { FetchStatus } from "../utils/FetchStatus";
import { Status } from "./SignupForm";

interface Props {
  onConfirm(signatureId?: string): void;
  onClose(): void;
  status: Status;
  show: boolean;
  requireSignature: boolean;
  postingTitle?: string;
}

interface State {
  show: boolean;
  order?: Order;
  submitting: boolean;
  signatureStatus: FetchStatus;
  message: string;
  link?: string;
  autoLaunch: boolean;
  orderRef: string;
  qrCodeBase64?: string;
  qrState?: string;
  collectStatus?: CollectStatus;
}

export default class ConfirmModal extends React.PureComponent<Props, State> {
  private readonly orderRefSessionStorageKey = "rebel.orderRefSignaturebankid.";

  state = {
    show: false,
    submitting: false,
    message: "",
    signatureStatus: !!sessionStorage.getItem(this.orderRefSessionStorageKey)
      ? FetchStatus.FETCHING
      : FetchStatus.IDLE,
    autoLaunch: isMobile,
  } as State;

  private readonly authenticationClient: AuthenticationClient =
    new AuthenticationClient();

  private posting = false;
  private errorCount = 0;

  componentDidMount() {
    let params = parseQueryString(window.location.search);

    let orderRef =
      (params && params.get("orderRef")) ||
      sessionStorage.getItem(this.orderRefSessionStorageKey);

    if (orderRef) {
      console.debug("Found orderRef on mount", orderRef);
      this.posting = true;
      this.setState({ signatureStatus: FetchStatus.FETCHING, orderRef });
      this.checkStatus(orderRef);
      sessionStorage.removeItem(this.orderRefSessionStorageKey);
    }
  }

  render() {
    let title: string;
    let body: React.ReactNode;
    let footer: React.ReactNode;

    switch (this.props.status) {
      case Status.CONFIRM: {
        if (
          !this.props.requireSignature ||
          this.state.signatureStatus == FetchStatus.IDLE
        ) {
          title = "Stämmer era kontaktuppgifter?";
          body = (
            <>
              <div className="form-group">
                <label className="control-label">Mobilnummer</label>
                <div className="textField">
                  {this.state.order?.phone || "Inget mobilnummer"}
                </div>
              </div>
              <div className="form-group">
                <label className="control-label mt-0">Epostadress</label>
                <div className="textField">
                  {this.state.order?.email || "Ingen epostadress"}
                </div>
              </div>
            </>
          );
          footer = this.props.requireSignature ? (
            <>
              {this.state.signatureStatus == FetchStatus.IDLE ? (
                <>
                  <button
                    className="btn btn-submit btn-lg mb-3"
                    onClick={() => this.initiateSignature(this.state.order)}>
                    Signera med BankId{" "}
                    <img src="/images/bankid-logo-white.svg" />
                  </button>
                  <br />
                  <a href="#" onClick={this.handleClose}>
                    Ändra
                  </a>
                </>
              ) : (
                <>
                  <p>{this.state.message}</p>
                </>
              )}
            </>
          ) : (
            <>
              <button
                className="btn btn-submit btn-lg mb-3"
                onClick={this.handleConfirm}>
                Allt ser ok ut
              </button>
              <br />
              <a href="#" onClick={this.handleClose}>
                Ändra
              </a>
            </>
          );
        } else {
          if (this.state.signatureStatus == FetchStatus.ERROR) {
            title = "Signatur från BankID misslyckades";
            body = (
              <div className="center-block">
                <p>{this.state.message}</p>
              </div>
            );
            footer = (
              <>
                <button
                  className="btn btn-submit btn-lg mb-3 mt-0"
                  onClick={() => this.initiateSignature(this.state.order)}>
                  Försök igen <img src="/images/bankid-logo-white.svg" />
                </button>
                <br />
                <a href="#" onClick={this.handleClose}>
                  Avbryt
                </a>
              </>
            );
          } else {
            title = "Väntar på signatur från BankID";
            body = (
              <div className="center-block">
                {this.state.qrCodeBase64 ? (
                  <img
                    className="w-50"
                    src={"data:image/png;base64, " + this.state.qrCodeBase64}
                  />
                ) : (
                  <>
                    <div className="spinner"></div>
                    {this.state.link && (
                      <>
                        <a
                          href={this.state.link}
                          className="btn btn-submit btn-lg">
                          Öppna BankID
                        </a>
                      </>
                    )}
                  </>
                )}
                <p>{this.state.message}</p>
              </div>
            );
            footer = null;
          }
        }

        break;
      }
      case Status.POSTING: {
        title = this.props.postingTitle ?? "Beställer ren, enkel och billig el";
        body = (
          <div className="center-block">
            <div className="spinner"></div>
          </div>
        );
        footer = null;
        break;
      }
      case Status.DONE: {
        title = "Din beställning är nu klar";
        body = <div className="center-block">Nu kommer allt det roliga...</div>;
        footer = null;
        break;
      }
      case Status.ERROR: {
        title = "Något gick fel";
        body = <div className="center-block">Något oväntat har hänt...</div>;
        footer = (
          <>
            <button
              className="btn btn-submit btn-lg mb-3 mt-0"
              onClick={this.handleConfirm}>
              Försök igen
            </button>
            <br />
            <a href="#" onClick={this.handleClose}>
              Ångra
            </a>
          </>
        );
        break;
      }
    }

    return (
      <Modal show={this.props.show} onHide={this.handleClose} backdrop="static">
        <Modal.Body>
          <p className="subheading">{title}</p>
          {body}
          {footer}
        </Modal.Body>
      </Modal>
    );
  }

  handleClose = () => {
    this.props.onClose();
  };

  show = (order: Order) => {
    this.setState({ order });
  };

  initiate = (order: Order) => {
    this.setState({ order });
    this.initiateSignature(order);
  };

  handleConfirm = () => {
    this.props.onConfirm();
  };

  initiateSignature = async (order: Order) => {
    console.debug("Initiate signature for order", order, this.state.order);

    if (this.posting) {
      return;
    }
    this.posting = true;
    this.errorCount = 0;
    this.setState({
      signatureStatus: FetchStatus.FETCHING,
      link: null,
      message: null,
    });

    try {
      let response = await this.authenticationClient.initiateSignature(
        SignInitiateRequest.fromJS({
          order,
        })
      );

      if (this.state.autoLaunch) {
        let link =
          (isIOS ? "https://app.bankid.com/" : "bankid:///") +
          "?autostarttoken=" +
          response.autoStartToken +
          "&redirect=null";

        if (isIOS) {
          sessionStorage.setItem(
            this.orderRefSessionStorageKey,
            response.orderRef
          );
          this.setState({ link });
        } else {
          window.location.href = link;
        }
      } else {
        console.debug("Stored qr state", {
          qrCodeBase64: response.qrCodeBase64Encoded,
          qrState: response.state,
        });
        this.setState({
          qrCodeBase64: response.qrCodeBase64Encoded,
          qrState: response.state,
          message: "Starta BankID-appen",
        });
      }

      setTimeout(() => {
        this.checkStatus(response.orderRef);
      }, 500);

      console.debug("Signature initiated", response);
    } catch (error) {
      this.posting = false;
      console.error("Signature failed", error);
      this.setState({ signatureStatus: FetchStatus.ERROR });
    }
  };

  checkStatus = async (orderRef: string) => {
    console.debug("Checking signature status");
    try {
      let response = await this.authenticationClient.status(
        orderRef,
        this.state.autoLaunch ? "" : this.state.qrState
      );
      console.debug("Got signature status", response);

      switch (response.status) {
        case CollectStatus.Pending: {
          this.setState({
            message: response.message,
            qrCodeBase64: response.qrCodeBase64Encoded,
          });
          setTimeout(() => {
            this.checkStatus(orderRef);
          }, 2000);
          break;
        }
        case CollectStatus.Failed: {
          this.posting = false;
          this.setState({
            message: response.message,
            signatureStatus: FetchStatus.ERROR,
            qrCodeBase64: null,
            link: null,
          });
          break;
        }
        case CollectStatus.Complete: {
          this.props.onConfirm(response.signatureId);
          break;
        }
        default: {
          console.error("Signature failed", response);
          this.posting = false;
          this.setState({
            message:
              response.message ||
              "Någonting oväntat hände, var vänlig försök igen.",
            signatureStatus: FetchStatus.ERROR,
            qrCodeBase64: null,
            link: null,
          });
        }
      }
    } catch (error) {
      console.error("Signature status failed", error);

      if (this.errorCount > 1) {
        sessionStorage.removeItem(this.orderRefSessionStorageKey);
        console.error("Signature status failed", error);
        this.posting = false;
        this.setState({
          message: "Någonting oväntat hände, var vänlig försök igen",
          signatureStatus: FetchStatus.ERROR,
        });
      } else {
        this.errorCount++;
        console.debug("Got error check status", error, this.errorCount);
        setTimeout(() => {
          this.checkStatus(orderRef);
        }, 2000);
      }
    }
  };
}
